import _ from 'lodash';
import * as React from 'react';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
// import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import useFetch from 'use-http';
import { useEffect, useState } from 'react';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useForm } from 'react-hook-form';
import Select, { StylesConfig } from 'react-select';
import TsdiClient from 'src/models/AP/Client';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import { ODataOptions, ODataFilter, ODataFilterOperators } from 'src/util/ODataBuilder';
import { ITableConfig } from 'src/components/common/table';
import Landman from 'src/models/Landman';
import ProspectDetails from 'src/models/projectSettings/TsdiProspect';

interface IClientProjectSearchProps {
    setIsCollapsed?: (isCollpased: boolean) => void;
    setSelectedClient?: (clientNumber: TsdiClient) => void;
    setSelectedProject?: (project: ProspectDetails) => void;
    setSelectedManager?: (managerNumber: string) => void;
    setSelectedPayPeriod?: (payPeriod: string) => void;
    clients?: Array<TsdiClient>;
    projects?: boolean;
    payPeriods?: Array<{id: number | string, name: string}>;
    managers?: Landman[];    
    runQuery?: () => void;
    size?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14;
}

interface IOptionType {
    value: string;
    label: string;
  }

const ClientProjectSearch = (props: IClientProjectSearchProps) => {
    const { get, response } = useFetch();
    const { register, errors } = useForm();
    const [clientSearchTerm, setClientSearchTerm] = useState<string>(undefined);
    const [fetchNextClientPage, setFetchNextClientPage] = useState<boolean>(true);
    const [clientResultsPlaceholder, setClientResultsPlaceholder] = useState<string>('Select Client'); // Used to inform user about personnel list results
    const [projectResultsPlaceholder, setProjectResultsPlaceholder] = useState<string>('Select Project'); // Used to inform user about personnel list results
    const [payPeriodResultsPlaceholder, setPayPeriodResultsPlaceholder] = useState<string>('Select Pay Period'); // Used to inform user about personnel list results
    const [managerResultsPlaceholder, setManagerResultsPlaceholder] = useState<string>('Select Manager'); // Used to inform user about personnel list results
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false); // Used to collapse the Search Personnel Panel to make room for other components
    const [isLoading, setIsLoading] = useState<boolean>(true); // isLoading is true if fetching personnel still
    const [isUserSearchingClients, setIsUserSearchingClients] = useState<boolean>(false); // true if the search text is not empty
    const [isUserSearchingProjects, setIsUserSearchingProjects] = useState<boolean>(false); // true if the search text is not empty
    const [isUserSearchingManagers, setIsUserSearchingManagers] = useState<boolean>(false); // true if the search text is not empty
    const [isUserSearchingPayPeriods, setIsUserSearchingPayPeriods] = useState<boolean>(false); // true if the search text is not empty
    const [clients, setClients] = useState<Array<TsdiClient>>(undefined);
    const [clientSearchDataOptions, setClientSearchDataOptions] = useState<ODataOptions>(undefined);
    const [projects, setProjects] = useState<Array<ProspectDetails>>(undefined);
    const [managers, setManagers] = useState<Array<Landman>>(undefined);
    const [payPeriods, setPayPeriods] = useState<Array<{id: number | string, name: string}>>(undefined);
    const [selectedClient, setSelectedClient] = useState<number>(undefined);
    const [debouncedClient, setDebouncedClient] = useState<number>(undefined);
    const [selectedProject, setSelectedProject] = useState<number>(undefined);
    const [selectedPayPeriod, setSelectedPayPeriod] = useState<string>(undefined);
    const [clientSearchResults, setClientSearchResults] = useState<Array<{ value: string; label: string }>>(undefined); // The clients filtered by search (Name)
    const [projectSearchResults, setProjectSearchResults] = useState<Array<{ value: string; label: string }>>(undefined); // The clients filtered by search (Name)
    const [managerSearchResults, setManagerSearchResults] = useState<Array<{ value: string; label: string }>>(undefined); // The clients filtered by search (Name)
    const [payPeriodSearchResults, setPayPeriodSearchResults] = useState<Array<{ value: string; label: string }>>(undefined); // The clients filtered by search (Name)

    const customStyles: StylesConfig<IOptionType, false> = {
        control: (provided) => ({
          ...provided,
          minHeight: '30px',    // Set a smaller minHeight
          height: '30px',       // Force a smaller height
          padding: '0px',       // Remove internal padding to make it smaller
        }),
        valueContainer: (provided) => ({
          ...provided,
          padding: '0px 8px',   // Adjust padding to make the height smaller
          height: '30px',       // Force height for the container
        }),
        input: (provided) => ({
          ...provided,
          margin: '0px',        // Ensure no margins are added
          padding: '0px',       // Ensure no padding is added
        }),
        indicatorsContainer: (provided) => ({
          ...provided,
          height: '30px',       // Force the height of the dropdown indicators
        }),
        dropdownIndicator: (provided) => ({
          ...provided,
          padding: '0px',       // Remove extra padding from the dropdown arrow
        }),
        clearIndicator: (provided) => ({
          ...provided,
          padding: '0px',       // Remove extra padding from the clear indicator
        }),
      };

    useEffect(() => {
        initializeClientSearchOptions();
    }, []);

    useEffect(() => {
        if (!!clients && clients?.length > 0) {
            if (!!debouncedClient) {
                setSelectedClient(debouncedClient);
            }
        }
    }, [clients]);

    useEffect(() => {
        if (clientSearchDataOptions) {
            // Todo: setSelectedClient to debounced client number
            getClients();
        }
    }, [clientSearchDataOptions]);

    const incrementClients = () => {
        if (!!fetchNextClientPage) {
            let newSearchOptions = _.cloneDeep(clientSearchDataOptions);
            newSearchOptions.incrementPageNumber();
            setClientSearchDataOptions(newSearchOptions);
        }
    };

    useEffect(() => {
        if (props.setIsCollapsed) {
            props.setIsCollapsed(isCollapsed);
        }
    }, [isCollapsed]);

    useEffect(() => {
        if (!!props?.managers) {
            setManagers(props.managers);
        }
    }, [props.managers]);

    useEffect(() => {
        if (!!props?.payPeriods) {
            setPayPeriods(props.payPeriods);
        }
    }, [props.payPeriods]);

    // SelectedClient will change when the user clicks on a user or on initial render, if url contains an id
    useEffect(() => {
        if (selectedClient) {
            const clientIndex = clients.findIndex((c) => c.clientNumber === selectedClient);
            props.setSelectedClient(clients[clientIndex]);
            if (props?.projects) {
                setProjectSearchResults(undefined);
                getProjects();
            }
        }
    }, [selectedClient]);

    // SelectedProject will change when the user clicks on a user or on initial render, if url contains an id
    useEffect(() => {
        if (selectedProject) {
            const project = projects.find((p) => p.number === selectedProject);
            props.setSelectedProject(project);
        }
    }, [selectedProject]);

    useEffect(() => {
        if (selectedPayPeriod) {
            props.setSelectedPayPeriod(selectedPayPeriod);
        }
    }, [selectedPayPeriod]);

    // clientSearchResults is updated when the user clicks, focuses, or types in the Search Bar
    useEffect(() => {
        if (clientSearchResults?.length) {
            setIsLoading(false);
        }

        setClientResultsPlaceholder(`Select Client (${clientSearchResults?.length !== undefined ? clientSearchResults.length : 0})`);
    }, [clientSearchResults]);

    // projectSearchResults is updated when the user clicks, focuses, or types in the Search Bar
    useEffect(() => {
        if (projectSearchResults?.length) {
            setIsLoading(false);
        }

        setProjectResultsPlaceholder(`Select Project (${projectSearchResults?.length !== undefined ? projectSearchResults.length : 0})`);
    }, [projectSearchResults]);

    useEffect(() => {
        if (managerSearchResults?.length) {
            setIsLoading(false);
        }

        setManagerResultsPlaceholder(`Select Manager (${managerSearchResults?.length})`);
    }, [managerSearchResults]);

    useEffect(() => {
        if (payPeriodSearchResults?.length) {
            setIsLoading(false);
        }

        setPayPeriodResultsPlaceholder(`Select Pay Period (${payPeriodSearchResults?.length})`);
    }, [payPeriodSearchResults]);

    // clientList should only change once, on initial render (it contains the full list of clients)
    useEffect(() => {
        if (clients?.length) {

            let list = clients?.map((c) => {
                return { 
                    label: (c.clientName ? `(${c.clientName}) ` : ''), 
                    value: `${c.clientNumber}` };
            });
            setClientSearchResults(list);
        }
    }, [clients]);

    // projectList should only change once, on initial render (it contains the full list of clients)
    useEffect(() => {
        if (projects?.length) {

            let list = projects?.map(p => {
                return { 
                    label: (p.name ? `(${p.name}) ` : ''), 
                    value: `${p.number}` };
            });
            setProjectSearchResults(list);
        }
    }, [projects]);

    useEffect(() => {
        if (managers?.length) {

            let list = managers?.map(m => {
                return { 
                    label: (!!m.lastName && !!m.firstName ? `(${m.firstName + ' ' + m.lastName})` : ''), 
                    value: `${m.lmsNumber}` };
            });
            setManagerSearchResults(list);
        }
    }, [managers]);

    useEffect(() => {
        if (payPeriods?.length) {

            let list = payPeriods?.map(p => {
                return { 
                    label: (!!p.name ? `(${p.name})` : ''), 
                    value: `${p.id}` };
            });
            setPayPeriodSearchResults(list);
        }
    }, [payPeriods]);

    const initializeClientSearchOptions = () => {
        const tableConfig: ITableConfig = {
            rowCount: 250,
            idField: 'CL_NO',
            pageNumber: 1,
            columns: []
        };
        const options = ODataOptions.from(tableConfig);
        setClientSearchDataOptions(options);
    };

    const getClients = async () => {
        let result = await get(`/v1.0/AccountsPayable/clients${clientSearchDataOptions.toString()}`);

        if (response.ok) {
            if (clientSearchDataOptions.getPageNumber() > 1) {
                let tempResult = _.cloneDeep(clients);
                tempResult = [...tempResult].concat([...result.items]);
                setClients([]);
                setClients(tempResult);
            } else {
                setClients(result.items);
            }
            setFetchNextClientPage(result.pageNumber * result.pageSize < result.totalRecords);
        } else {
            console.log('Unable to get client data!!!');
        }
    };

    const getProjects = async () => {
        if (clients === undefined || clients?.length < 1) {
            return;
        }
        const clientIndex = clients?.findIndex((c) => c?.clientNumber === selectedClient);
        if (clientIndex === -1) {
            return;
        }
        let result = await get(`/v1.0/ClientProjectMaintenance/projects/byclient/${clients[clientIndex].clientNumber}`);

        if (response.ok) {
            setProjects([]);
            setProjects(result);
        } else {
            setProjects([]);
            console.log('Unable to get client data!');
        }
    };

    const search = _.debounce((inputText: string) => {
        if (!!inputText && inputText.length > 0) {            
            setClientSearchTerm(inputText);
        }
    }, 800);

    useEffect(() => {
        if (!!clientSearchTerm || clientSearchTerm === '') {
            resetClientSearchOptions();
        }
    }, [clientSearchTerm]);

    const resetClientSearchOptions = () => {
        setFetchNextClientPage(true);
        let newClientSearchOptions = _.cloneDeep(clientSearchDataOptions);
        newClientSearchOptions.clearFilters();
        if (typeof clientSearchTerm === 'string') {
            newClientSearchOptions?.addFilter(new ODataFilter('NAME1', ODataFilterOperators.Contains, clientSearchTerm !== undefined ? clientSearchTerm : ''));
        }
        setClientSearchDataOptions(newClientSearchOptions);
    };

    const handleClientsListScrollToBottom = () => {
        incrementClients();
    };

    const handleClientListSearchChanged = (inputText: string) => {
        search(inputText);
    };

    const handleClientListSelectChanged = (clientNumber: string) => {
        setClientSearchTerm(undefined);
        const clientNumberNumber = Number(clientNumber);
        if (!isNaN(clientNumberNumber)) {
            setDebouncedClient(clientNumberNumber);
            setSelectedClient(clientNumberNumber);
        }
    };

    if (isLoading) {
        return (
            <div style={{ textAlign: 'center' }}>
                <h2>Loading Clients...</h2>
                <LoadingSpinner />
            </div>
        );
    } else if (!isCollapsed) {
        return (
            <Page style={{justifyContent: 'center'}}>
                <Page.Section width={!!props?.size ? props.size : 10}>
                    <Panel backgroundColor="#D3D3D3">
                        <Panel>
                            <div className="form-row" style={{ padding: '0', margin: '0' }}>
                            {!!props?.payPeriods && <div style={{ width: props?.size === 10 ? '25%' : '50%'}}>
                                    <Select
                                        className="personnel-search-list"
                                        name="payPeriodsearchResults"
                                        placeholder={payPeriodResultsPlaceholder}
                                        options={payPeriodSearchResults}
                                        onChange={(e: IOptionType) => setSelectedPayPeriod(e.value)}
                                        onMenuOpen={() => setIsUserSearchingPayPeriods(true)}
                                        onMenuClose={() => setIsUserSearchingPayPeriods(false)}
                                        onBlur={() => setIsUserSearchingPayPeriods(false)}
                                        register={register}
                                        errors={errors}
                                        menuIsOpen={isUserSearchingPayPeriods}
                                        isSearchable={true}
                                        styles={customStyles}
                                        title={`Pay Period`}
                                    />
                                </div>}
                                {!!props?.managers && <div style={{ width: props?.size === 10 ? '25%' : '25%', paddingLeft: '4px'}}>
                                    <Select
                                        className="personnel-search-list"
                                        name="managerSearchResults"
                                        placeholder={managerResultsPlaceholder}
                                        options={managerSearchResults}
                                        onChange={(e: IOptionType) => props?.setSelectedManager(e.value)}
                                        onMenuOpen={() => setIsUserSearchingManagers(true)}
                                        onMenuClose={() => setIsUserSearchingManagers(false)}
                                        onBlur={() => setIsUserSearchingManagers(false)}
                                        register={register}
                                        errors={errors}
                                        menuIsOpen={isUserSearchingManagers}
                                        isSearchable={true}
                                        styles={customStyles}
                                    />
                                </div>}
                                {!!props?.clients && <div style={{ width: props?.size === 10 ? '25%' : '35%', paddingLeft: '4px'}}>
                                    <Select
                                        id={'clientSearchResults'}
                                        className="personnel-search-list"
                                        name="clientSearchResults"
                                        placeholder={clientResultsPlaceholder}
                                        options={clientSearchResults}
                                        onChange={(e: IOptionType) => handleClientListSelectChanged(e.value)}
                                        onMenuOpen={() => setIsUserSearchingClients(true)}
                                        onMenuClose={() => setIsUserSearchingClients(false)}
                                        onBlur={() => setIsUserSearchingClients(false)}
                                        register={register}
                                        errors={errors}
                                        menuIsOpen={isUserSearchingClients}
                                        isSearchable={true}
                                        styles={customStyles}
                                        onMenuScrollToBottom={handleClientsListScrollToBottom}
                                        onInputChange={handleClientListSearchChanged}
                                        onFocus={resetClientSearchOptions}
                                    />
                                </div>}
                                {!!props?.projects && <div style={{ width: props?.size === 10 ? '25%' : '35%', paddingLeft: '4px'}}>
                                    <Select
                                        className="personnel-search-list"
                                        name="projectSearchResults"
                                        placeholder={projectResultsPlaceholder}
                                        options={projectSearchResults}
                                        onChange={(e: IOptionType) => setSelectedProject(Number(e.value))}
                                        onMenuOpen={() => setIsUserSearchingProjects(true)}
                                        onMenuClose={() => setIsUserSearchingProjects(false)}
                                        onBlur={() => setIsUserSearchingProjects(false)}
                                        register={register}
                                        errors={errors}
                                        menuIsOpen={isUserSearchingProjects}
                                        isSearchable={true}
                                        styles={customStyles}
                                    />
                                </div>}
                                <Button
                                    text="Search"
                                    className="blue-hover-button"
                                    style={{ width: '10%', height: '3rem', verticalAlign: 'middle', marginLeft: '3rem', borderRadius: '5px' }}
                                    onClick={() => !!props?.runQuery ? props.runQuery() : {}}
                                />
                                <Button
                                    text="(-) Minimize"
                                    className="blue-hover-button"
                                    style={{ paddingLeft: '2px', width: '15%', height: '3rem', verticalAlign: 'middle', marginLeft: '3rem', borderRadius: '5px' }}
                                    disabled={isCollapsed}
                                    onClick={() => setIsCollapsed(true)}
                                />
                            </div>
                        </Panel>
                    </Panel>
                </Page.Section>
            </Page>
        );
    } else {
        return (
            <div style={{ textAlign: 'center' }}>
                <Button
                    text="(+) Search Filters"
                    className="blue-hover-button"
                    style={{ height: '2rem', margin: '0', padding: '.2rem' }}
                    onClick={() => setIsCollapsed(false)}
                />
            </div>
        );
    }
};

export default ClientProjectSearch;


// WEBPACK FOOTER //
// ./src/components/project-billing/components/ClientProjectSearch.tsx